import { useRef, useState } from 'react'
import { usePage } from '@inertiajs/react'
import { signUpPath } from '@/shared/likes/signUpPath'

// Anything that can be liked from a list: its own id plus the id of the
// current user's like (null when not liked).
type Likeable = { id: number; like_id: number | null }

const csrfToken = () =>
  document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""

/**
 * Optimistic like toggling for list views. Fires plain fetch POST/DELETE calls
 * against /likes and tracks the resulting like id locally, so a single card
 * updates without an Inertia page reload (which would reset map/scroll state).
 *
 * `type` is the likeable_type, e.g. "Event" or "Track".
 */
export function useLikes(type: string) {
  // Local overrides keyed by likeable id, layered over the server-provided like_id.
  const [overrides, setOverrides] = useState<Record<number, number | null>>({})
  // Likeable ids with a request in flight. A ref, not state: two clicks before a
  // re-render must both see the first one, or a double click on "remove"
  // (already cleared optimistically) would fire a POST and re-add the like.
  const pending = useRef(new Set<number>())
  const { props, url } = usePage()

  const likeIdFor = (item: Likeable) =>
    item.id in overrides ? overrides[item.id] : item.like_id

  const toggleLike = async (item: Likeable) => {
    if (!props.currentUser) {
      window.location.href = signUpPath(url)
      return
    }

    if (pending.current.has(item.id)) return
    pending.current.add(item.id)
    try {
      await sendToggle(item)
    } finally {
      pending.current.delete(item.id)
    }
  }

  const sendToggle = async (item: Likeable) => {
    const currentLikeId = likeIdFor(item)

    if (currentLikeId != null) {
      setOverrides(prev => ({ ...prev, [item.id]: null }))
      const res = await fetch(`/likes/${currentLikeId}`, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
      })
      if (!res.ok) setOverrides(prev => ({ ...prev, [item.id]: currentLikeId }))
    } else {
      const res = await fetch('/likes', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken(),
          Accept: 'application/json',
        },
        body: JSON.stringify({ like: { likeable_type: type, likeable_id: item.id } }),
      })
      if (res.ok) {
        const data = await res.json()
        setOverrides(prev => ({ ...prev, [item.id]: data.id ?? null }))
      }
    }
  }

  return { likeIdFor, toggleLike }
}
